import React, { FunctionComponent, useCallback, JSXElementConstructor } from 'react';
import Pen from 'app/components/svgs/draw/Pen';
import Eraser from 'app/components/svgs/draw/Eraser';
import { COLOR_PALETTE } from 'app/constants/ColorPalette';
import { TraceType } from 'app/models/rtc/draw/trace';
import { useCss } from 'app/utils/css';
import IconButton from 'app/components/presentation/buttons/IconButton';
import FlexMarginGroup from 'app/components/layout/FlexMarginGroup';
import { useI18n, tFunctionType } from 'app/components/localization';
import { useDraw } from 'app/components/rtc/draw/DrawContext';

interface IProps {
  setTool: (tool: TraceType) => void;
  activeTool: TraceType | null;
}

const DrawToolbox: FunctionComponent<IProps> = (props) => {
  const { isBlocked } = useDraw();
  if (isBlocked) {
    return null;
  }

  return <FlexMarginGroup vertical>
    <ToolboxButton tool={TraceType.Pencil} {...props} />
    <ToolboxButton tool={TraceType.Eraser} {...props} />
  </FlexMarginGroup>;
};

interface IToolboxButtonProps extends IProps {
  tool: TraceType,
}

interface IIconProps {
  color: string;
  backgroundColor: string;
  className?: string;
}

const ICON_PROPS: IIconProps = {
  color: COLOR_PALETTE.grey.white,
  backgroundColor: COLOR_PALETTE.grey.darker,
};

const ACTIVE_ICON_PROPS: IIconProps = {
  color: COLOR_PALETTE.grey.darker,
  backgroundColor: COLOR_PALETTE.grey.white,
};

interface IToolSpec {
  Icon: JSXElementConstructor<IIconProps>;
  getName: (t: tFunctionType) => string;
}

const ToolSpecs: { [K in TraceType]: IToolSpec } = {
  [TraceType.Pencil]: {
    Icon: Pen,
    getName: (t) => t('rtc.draw.toolbox.pencil'),
  },
  [TraceType.Eraser]: {
    Icon: Eraser,
    getName: (t) => t('rtc.draw.toolbox.eraser'),
  },
};

const ToolboxButton: FunctionComponent<IToolboxButtonProps> = ({ tool, setTool, activeTool }) => {
  const css = useCss();
  const { Icon, getName } = ToolSpecs[tool];
  const { t } = useI18n();

  const onClick = useCallback(() => setTool(tool), [setTool, tool]);

  return <IconButton
    onClick={onClick}
    overrideStyles={{ display: 'block' }}
    aria-label={getName(t)}
    title={getName(t)}
  >
    <Icon
      className={css('toolboxIcon', {
        display: 'block',
        width: '100%',
      })}
      {...activeTool === tool ? ACTIVE_ICON_PROPS : ICON_PROPS}
    />
  </IconButton>;
};

export default DrawToolbox;
